import {
  AlertCircle,
  CheckCircle2,
  Download,
  KeyRound,
  Loader2,
  PlayCircle,
} from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { useMatter } from '@/hooks/useMatter';
import { cn } from '@/lib/utils';
import type {
  FilevineIngestionStatus,
  FilevineSyncPlanListSchema,
} from '@/services/api/generated/filevineSyncService';
import { enhancedApi } from '@/services/api/generated/filevineSyncService';
import { useGetMatterDetailsQuery } from '@/services/api/mattersService';
import { useOnepasswordListItemsQuery } from '@/services/api/onePasswordService';

import { AddOnePasswordDialog } from '../firms/onepassword/AddOnePasswordDialog';
import { FilevineSyncPlanCard } from './FilevineSyncPlanCard';

const STALE_THRESHOLD_MS = 30 * 60 * 1000; // 30 minutes

type PlanStatus = 'pending' | 'syncing' | 'planning' | 'completed' | 'failed';

const PLAN_STEPS = [
  { key: 'syncing', label: 'Syncing with Filevine' },
  { key: 'planning', label: 'Generating sync plan' },
  { key: 'completed', label: 'Ready' },
] as const;

export const FilevineSyncUpdates: React.FC = () => {
  const { matterId } = useMatter();

  const { data: matter } = useGetMatterDetailsQuery(matterId);
  const firmId = matter?.firm?.id;
  const { data: onePasswordItems = [] } = useOnepasswordListItemsQuery(
    { firmId: firmId ?? '' },
    { skip: !firmId },
  );

  const { data: plans, isLoading } = enhancedApi.useFilevineSyncPlansListQuery(
    { matterId },
    {
      pollingInterval: 3000, // Poll every 3 seconds
      skip: !matterId,
    },
  );

  const [triggerSync, { isLoading: isTriggeringSync }] =
    enhancedApi.useFilevineSyncPlansTriggerMutation();

  const [triggerIngestion, { isLoading: isTriggeringIngestion }] =
    enhancedApi.useFilevineIngestionTriggerMutation();

  const [status, setStatus] = useState<FilevineIngestionStatus | null>(null);

  const { data: ingestionStatus, refetch: refetchIngestionStatus } =
    enhancedApi.useFilevineIngestionStatusQuery(
      { matterId },
      {
        pollingInterval: 5000,
        skip: !matterId || status !== 'IN_PROGRESS',
      },
    );

  useEffect(() => {
    if (ingestionStatus) {
      setStatus(ingestionStatus.status);
    }
  }, [ingestionStatus]);

  const handleTriggerSync = async () => {
    try {
      await triggerSync({ matterId }).unwrap();
      toast.success('Sync triggered successfully!');
    } catch (error) {
      toast.error('Failed to trigger sync');
    }
  };

  const handleTriggerIngestion = async () => {
    try {
      await triggerIngestion({ matterId }).unwrap();
      toast.success('Ingestion triggered successfully!');
      // Refetch status after a short delay to get the initial status
      setTimeout(() => {
        refetchIngestionStatus();
      }, 1000);
    } catch (error) {
      toast.error('Failed to trigger ingestion');
    }
  };

  // Check if firm has CMS credentials in OnePassword
  const hasCmsCredentials = onePasswordItems.some(
    (item) => item.type === 'cms_credentials',
  );

  if (isLoading) {
    return (
      <div className="flex flex-col h-full items-center justify-center">
        <p className="text-gray-500">Loading Filevine sync status...</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {plans && (
        <FilevineSyncContent
          plans={plans}
          onTriggerSync={handleTriggerSync}
          isTriggeringSync={isTriggeringSync}
          hasCmsCredentials={hasCmsCredentials}
          firmId={matter?.firm?.id}
          onTriggerIngestion={handleTriggerIngestion}
          isTriggeringIngestion={isTriggeringIngestion}
          ingestionStatus={ingestionStatus}
        />
      )}
    </div>
  );
};

const FilevineSyncContent = ({
  plans,
  onTriggerSync,
  isTriggeringSync,
  hasCmsCredentials,
  firmId,
  onTriggerIngestion,
  isTriggeringIngestion,
  ingestionStatus,
}: {
  plans: FilevineSyncPlanListSchema[];
  onTriggerSync: () => void;
  isTriggeringSync: boolean;
  hasCmsCredentials: boolean;
  firmId?: string;
  onTriggerIngestion: () => void;
  isTriggeringIngestion: boolean;
  ingestionStatus?: {
    status: 'IN_PROGRESS' | 'COMPLETED' | 'FAILED';
    current_phase?: string | null;
  };
}) => {
  const [isAddOnePasswordOpen, setIsAddOnePasswordOpen] = React.useState(false);
  const { mostRecentPlan, isStale, actionsCount, planStatus } = useMemo(() => {
    if (plans.length === 0) {
      return {
        mostRecentPlan: null,
        isStale: false,
        actionsCount: 0,
        planStatus: null as PlanStatus | null,
      };
    }

    // Sort by created_at descending to get most recent
    const sorted = [...plans].sort(
      (a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
    );
    const recent = sorted[0];

    // Check if plan is stale (older than 5 minutes)
    const planAge = Date.now() - new Date(recent.created_at).getTime();
    const stale = planAge > STALE_THRESHOLD_MS;

    // Count active actions (not completed or rejected)
    const count = recent.actions?.length ?? 0;
    const status = recent.status.toLowerCase() as PlanStatus;

    return {
      mostRecentPlan: recent,
      isStale: stale,
      actionsCount: count,
      planStatus: status,
    };
  }, [plans]);

  // Show progress bar for in-progress plans
  const showProgressBar =
    planStatus &&
    (planStatus === 'syncing' || planStatus === 'planning') &&
    !isStale;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <span className="text-2xl font-semibold">Filevine Sync</span>
        <div className="flex gap-2">
          <IngestNowButton
            onClick={onTriggerIngestion}
            isTriggering={isTriggeringIngestion}
            status={ingestionStatus}
          />
          <Button
            onClick={onTriggerSync}
            disabled={isTriggeringSync}
            variant="default"
            size="sm"
          >
            <PlayCircle className="h-4 w-4 mr-2" />
            {isTriggeringSync ? 'Syncing...' : 'Sync Now'}
          </Button>
        </div>
      </div>

      {/* Missing CMS Credentials Alert */}
      {!hasCmsCredentials && firmId && (
        <div className="border border-amber-300 bg-amber-50 rounded-md p-4">
          <div className="flex items-start gap-3">
            <KeyRound className="h-5 w-5 text-amber-600 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-amber-800 mb-1">
                CMS Credentials Required
              </p>
              <p className="text-sm text-amber-700 mb-3">
                To sync with Filevine, you need to add CMS credentials for this
                firm in OnePassword.
              </p>
              <div className="flex gap-2">
                <Button
                  onClick={() => setIsAddOnePasswordOpen(true)}
                  variant="outline"
                  size="sm"
                  className="border-amber-300 text-amber-800 hover:bg-amber-100"
                >
                  <KeyRound className="h-4 w-4 mr-2" />
                  Add CMS Credentials
                </Button>
                <Link to={`/firms/${firmId}/onepassword`}>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-amber-800 hover:bg-amber-100"
                  >
                    View Firm Settings
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Progress Stepper */}
      {showProgressBar && planStatus && (
        <ProgressStepper currentStatus={planStatus} />
      )}

      {/* No plan yet */}
      {!mostRecentPlan && (
        <div className="text-center text-gray-500 py-8">
          <p>No sync plan generated yet.</p>
          <p className="text-sm mt-2">
            Click &quot;Sync Now&quot; to check for updates from Filevine.
          </p>
        </div>
      )}

      {/* Plan with actions - always show, but indicate if stale */}
      {mostRecentPlan && actionsCount > 0 && (
        <>
          {isStale && (
            <div className="border border-yellow-300 bg-yellow-50 rounded-md p-4 mb-4">
              <p className="text-sm text-yellow-800 mb-2">
                This sync plan is more than 30 minutes old. Approve/reject
                actions are disabled.
              </p>
              <Button
                onClick={onTriggerSync}
                disabled={isTriggeringSync}
                variant="outline"
                size="sm"
              >
                <PlayCircle className="h-4 w-4 mr-2" />
                Refresh Sync Plan
              </Button>
            </div>
          )}
          <FilevineSyncPlanCard plan={mostRecentPlan} isStale={isStale} />
        </>
      )}

      {/* Failed plan - show error state */}
      {mostRecentPlan && planStatus === 'failed' && (
        <div className="border border-red-300 bg-red-50 rounded-md p-6 text-center">
          <div className="flex justify-center mb-3">
            <AlertCircle className="h-12 w-12 text-red-500" />
          </div>
          <p className="text-red-800 font-medium mb-2">Sync Failed</p>
          <p className="text-sm text-red-600 mb-4">
            The sync plan failed to complete. Please try syncing again.
          </p>
          <p className="text-xs text-red-500 mb-4">
            Failed at{' '}
            {new Date(mostRecentPlan.created_at).toLocaleString('en-US', {
              month: 'short',
              day: 'numeric',
              hour: 'numeric',
              minute: '2-digit',
            })}
          </p>
          <Button
            onClick={onTriggerSync}
            disabled={isTriggeringSync}
            variant="outline"
            size="sm"
          >
            <PlayCircle className="h-4 w-4 mr-2" />
            Retry Sync
          </Button>
        </div>
      )}

      {/* No actions - show "up to date" message */}
      {mostRecentPlan && actionsCount === 0 && planStatus !== 'failed' && (
        <div className="text-center text-gray-500 py-8">
          <p className="text-sm text-gray-600">
            Last synced{' '}
            {new Date(mostRecentPlan.created_at).toLocaleString('en-US', {
              month: 'short',
              day: 'numeric',
              hour: 'numeric',
              minute: '2-digit',
            })}
          </p>
          <p className="mt-2 font-medium">Everything is up to date!</p>
          {isStale && (
            <p className="text-sm mt-2">
              Click &quot;Sync Now&quot; to check for new updates from Filevine.
            </p>
          )}
        </div>
      )}

      {/* Add OnePassword Dialog */}
      {firmId && (
        <AddOnePasswordDialog
          isOpen={isAddOnePasswordOpen}
          onClose={() => setIsAddOnePasswordOpen(false)}
          firmId={firmId}
          onAdded={() => setIsAddOnePasswordOpen(false)}
        />
      )}
    </div>
  );
};

const IngestNowButton: React.FC<{
  onClick: () => void;
  isTriggering: boolean;
  status?: {
    status: 'IN_PROGRESS' | 'COMPLETED' | 'FAILED';
    current_phase?: string | null;
  };
}> = ({ onClick, isTriggering, status }) => {
  const getButtonContent = () => {
    if (isTriggering || status?.status === 'IN_PROGRESS') {
      return {
        icon: <Loader2 className="h-4 w-4 mr-2 animate-spin" />,
        text: status?.current_phase || 'Ingesting...',
        variant: 'secondary' as const,
        disabled: true,
      };
    }

    if (status?.status === 'COMPLETED') {
      return {
        icon: <CheckCircle2 className="h-4 w-4 mr-2" />,
        text: 'Ingested',
        variant: 'outline' as const,
        disabled: false,
      };
    }

    if (status?.status === 'FAILED') {
      return {
        icon: <AlertCircle className="h-4 w-4 mr-2" />,
        text: 'Ingest Failed',
        variant: 'destructive' as const,
        disabled: false,
      };
    }

    return {
      icon: <Download className="h-4 w-4 mr-2" />,
      text: 'Ingest Now',
      variant: 'outline' as const,
      disabled: false,
    };
  };

  const { icon, text, variant, disabled } = getButtonContent();

  return (
    <Button
      onClick={onClick}
      disabled={disabled}
      variant={variant}
      size="sm"
      className={cn(
        status?.status === 'COMPLETED' && 'text-green-700 border-green-300',
      )}
    >
      {icon}
      {text}
    </Button>
  );
};

const ProgressStepper: React.FC<{ currentStatus: PlanStatus }> = ({
  currentStatus,
}) => {
  const getStepState = (
    stepKey: string,
  ): 'completed' | 'active' | 'pending' => {
    const currentIndex = PLAN_STEPS.findIndex((s) => s.key === currentStatus);
    const stepIndex = PLAN_STEPS.findIndex((s) => s.key === stepKey);

    if (currentStatus === 'failed') {
      return stepIndex <= currentIndex ? 'completed' : 'pending';
    }

    if (stepIndex < currentIndex) return 'completed';
    if (stepIndex === currentIndex) return 'active';
    return 'pending';
  };

  return (
    <div className="w-full py-4">
      <div className="flex items-center justify-between">
        {PLAN_STEPS.map((step, index) => {
          const state = getStepState(step.key);
          const isLast = index === PLAN_STEPS.length - 1;

          return (
            <React.Fragment key={step.key}>
              {/* Step */}
              <div className="flex flex-col items-center gap-2 flex-1">
                {/* Icon */}
                <div
                  className={cn(
                    'flex items-center justify-center w-8 h-8 rounded-full border-2',
                    state === 'completed' &&
                      'bg-green-500 border-green-500 text-white',
                    state === 'active' &&
                      'bg-blue-500 border-blue-500 text-white',
                    state === 'pending' &&
                      'bg-white border-gray-300 text-gray-400',
                  )}
                >
                  {state === 'completed' && (
                    <CheckCircle2 className="h-5 w-5" />
                  )}
                  {state === 'active' && (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  )}
                  {state === 'pending' && (
                    <div className="w-2 h-2 rounded-full bg-gray-300" />
                  )}
                  {currentStatus === 'failed' && state === 'completed' && (
                    <AlertCircle className="h-5 w-5" />
                  )}
                </div>

                {/* Label */}
                <p
                  className={cn(
                    'text-xs text-center',
                    state === 'active' && 'font-medium text-gray-900',
                    state === 'completed' && 'text-gray-600',
                    state === 'pending' && 'text-gray-400',
                  )}
                >
                  {step.label}
                </p>
              </div>

              {/* Connector Line */}
              {!isLast && (
                <div className="flex-1 h-0.5 mx-2 -mt-8">
                  <div
                    className={cn(
                      'h-full transition-all duration-500',
                      state === 'completed' ? 'bg-green-500' : 'bg-gray-300',
                    )}
                  />
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};
